/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useRef, useState } from 'react';
import APIError from '@api/errors/APIError';
import HundredBricksAPIError from '@lib/hundredBricksAPI/HundredBricksAPIError';
import { useConnectionChange } from '@lib/connectionChange';
import useAuth from '../../lib/authentication/useAuth';
import ErrorWhileRequesting from './ErrorWhileRequesting';
const DEVICE_CONNECTION_CHANGE_ERROR = 'DEVICE_CHANGE_DETECTED';
const IP_CONNECTION_CHANGE_ERROR = 'IP_ADDRESS_CHANGE_DETECTED';
const validateChangeConnectionError = (error) => {
    // Validate legacy APIs
    const apiError = error;
    if (apiError?.code) {
        if (apiError?.code === DEVICE_CONNECTION_CHANGE_ERROR
            || apiError?.code === IP_CONNECTION_CHANGE_ERROR) {
            return true;
        }
    }
    // Validate lastest API
    const hundredBricksAPIError = error;
    if (hundredBricksAPIError?.cause?.code) {
        if (hundredBricksAPIError?.cause?.code === DEVICE_CONNECTION_CHANGE_ERROR
            || hundredBricksAPIError?.cause?.code === IP_CONNECTION_CHANGE_ERROR) {
            return true;
        }
    }
    return false;
};
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const useRequest = (callback) => {
    const auth = useAuth();
    const { setConnectionChangeData, hasConnectionChangeError, registerRequestStart, registerRequestEnd } = useConnectionChange();
    const [requestData, setRequestData] = useState({
        data: undefined,
        isLoading: false,
        error: undefined
    });
    const hasConnectionChangeErrorRef = useRef(hasConnectionChangeError);
    hasConnectionChangeErrorRef.current = hasConnectionChangeError;
    const clearConnectionChangeErrorIfNeeded = useCallback(() => {
        if (hasConnectionChangeErrorRef.current) {
            setConnectionChangeData({
                error: undefined,
                hasConnectionChangeError: false
            });
        }
    }, []);
    const makeRequest = async (...args) => {
        let data;
        let error;
        let hadConnectionChange = false;
        let hadUnauthorized = false;
        registerRequestStart();
        setRequestData(prevData => ({ ...prevData, isLoading: true }));
        try {
            data = await callback(...args);
            if (auth.token)
                auth.updateSessionToken();
            clearConnectionChangeErrorIfNeeded();
        }
        catch (requestError) {
            hadConnectionChange = validateChangeConnectionError(requestError);
            if (hadConnectionChange) {
                setConnectionChangeData({
                    error: requestError,
                    hasConnectionChangeError: hadConnectionChange
                });
                return;
            }
            // APIError should be deprecated
            if (requestError instanceof APIError
                || requestError instanceof HundredBricksAPIError) {
                hadUnauthorized = requestError.isUnauthorized();
                if (!hadUnauthorized) {
                    clearConnectionChangeErrorIfNeeded();
                }
                error = requestError;
            }
            else {
                clearConnectionChangeErrorIfNeeded();
                error = new ErrorWhileRequesting(requestError);
            }
        }
        finally {
            // finally runs even on the early return above, so the batch coordinator
            // always learns the outcome of every request exactly once.
            registerRequestEnd(hadConnectionChange, hadUnauthorized);
            setRequestData(prevData => ({
                ...prevData, data, error, isLoading: false
            }));
        }
    };
    return [requestData.data, requestData.isLoading, requestData.error, makeRequest];
};
export default useRequest;
